import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, AlertTriangle, Info, X } from "lucide-react";

interface AnnouncementToastProps {
  type: "success" | "error" | "info" | "warning";
  message: string;
  onClose: () => void;
  duration?: number;
}

const combineClasses = (...classes: (string | boolean | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

const AnnouncementToast: React.FC<AnnouncementToastProps> = ({
  type,
  message,
  onClose,
  duration = 5000,
}) => {
  const [visible, setVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setIsLeaving(true);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(onClose, 300);
  };

  return createPortal(
    <div
      className={combineClasses(
        "fixed top-10 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-[480px]",
        "transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1.0)]",
        visible && !isLeaving
          ? "opacity-100 translate-y-0"
          : "opacity-0 -translate-y-4"
      )}
      role="alert"
      aria-live="polite"
    >
      <div
        className={combineClasses(
          "flex items-center gap-4 px-6 py-4 rounded-lg shadow-lg",
          "backdrop-blur-md border-l-4",
          "bg-white dark:bg-[#1c1c1e]",
          type === "success"
            ? "border-green-500"
            : type === "error"
            ? "border-red-500"
            : "border-blue-500"
        )}
      >
        {type === "success" && (
          <CheckCircle className="w-6 h-6 text-green-500" />
        )}
        {type === "error" && <AlertTriangle className="w-6 h-6 text-red-500" />}
        {type === "info" && <Info className="w-6 h-6 text-blue-500" />}
        {type === "warning" && (
          <AlertTriangle className="w-6 h-6 text-yellow-500" />
        )}

        <p className="text-base font-medium text-black dark:text-white break-words">
          {message}
        </p>

        <button
          onClick={handleClose}
          className="ml-auto p-1 rounded-full transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-white/10"
          aria-label="Close notification"
        >
          <X className="w-5 h-5 text-gray-500 dark:text-white" />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default AnnouncementToast;
